/**
 * Environment System Type Definitions
 * 
 * Types for time-of-day, weather, skybox, and atmospheric effects
 */

/**
 * Time of day periods
 */
export enum TimeOfDay {
    DAWN = "dawn",
    MORNING = "morning",
    NOON = "noon",
    AFTERNOON = "afternoon",
    DUSK = "dusk",
    EVENING = "evening",
    NIGHT = "night",
    MIDNIGHT = "midnight",
}

/**
 * Season types (affects lighting and atmosphere)
 */
export enum Season {
    SPRING = "spring",
    SUMMER = "summer",
    AUTUMN = "autumn",
    WINTER = "winter",
}

/**
 * Weather types
 */
export enum WeatherType {
    CLEAR = "clear",
    CLOUDY = "cloudy",
    OVERCAST = "overcast",
    RAIN = "rain",
    STORM = "storm",
    FOG = "fog",
    SNOW = "snow",
}

/**
 * Skybox configuration
 */
export interface SkyboxConfig {
    /** Type of skybox */
    type: "procedural" | "cubemap" | "hdri";
    /** Size of the skybox (default: 1000) */
    size?: number;
    /** Cubemap texture paths (for cubemap type) */
    texturePaths?: {
        px: string; // positive X
        nx: string; // negative X
        py: string; // positive Y
        ny: string; // negative Y
        pz: string; // positive Z
        nz: string; // negative Z
    };
    /** Single HDR environment map path (for hdri type) */
    hdriPath?: string;
    /** Procedural sky settings */
    proceduralSettings?: ProceduralSkySettings;
}

/**
 * Procedural sky parameters
 */
export interface ProceduralSkySettings {
    /** Turbidity (atmosphere clarity) - 2 = clear, 10 = hazy */
    turbidity: number;
    /** Rayleigh scattering coefficient */
    rayleigh: number;
    /** Mie scattering coefficient */
    mie: number;
    /** Mie directional scattering */
    mieDirectionalG: number;
    /** Luminance factor */
    luminance: number;
    /** Inclination of the sun (0 = horizon, 0.5 = zenith) */
    inclination: number;
    /** Azimuth angle of the sun */
    azimuth: number;
}

/**
 * Time of day lighting configuration
 */
export interface TimeOfDayConfig {
    /** Time period name */
    period: TimeOfDay;
    /** Start hour (0-24) */
    startHour: number;
    /** End hour (0-24) */
    endHour: number;
    /** Sun direction */
    sunDirection: { x: number; y: number; z: number };
    /** Sun color */
    sunColor: { r: number; g: number; b: number };
    /** Sun intensity */
    sunIntensity: number;
    /** Ambient color */
    ambientColor: { r: number; g: number; b: number };
    /** Ambient intensity */
    ambientIntensity: number;
    /** Sky color (for procedural sky) */
    skyColor: { r: number; g: number; b: number; a: number };
    /** Horizon color */
    horizonColor: { r: number; g: number; b: number };
    /** Ground color (for hemisphere light) */
    groundColor: { r: number; g: number; b: number };
    /** Fog color */
    fogColor: { r: number; g: number; b: number };
    /** Fog density */
    fogDensity: number;
    /** Shadow darkness (0 = no shadow, 1 = full black) */
    shadowDarkness: number;
    /** Procedural sky settings override */
    skySettings?: Partial<ProceduralSkySettings>;
}

/**
 * Time acceleration configuration
 */
export interface TimeAccelerationConfig {
    /** Acceleration rate for time scale changes (units per second) */
    accelerationRate?: number;
    /** Deceleration rate for time scale changes (units per second) */
    decelerationRate?: number;
    /** Minimum time scale allowed */
    minTimeScale?: number;
    /** Maximum time scale allowed */
    maxTimeScale?: number;
    /** Enable smooth transitions between time scales */
    smoothTransitions?: boolean;
}

/**
 * Environment configuration
 */
export interface EnvironmentConfig {
    /** Initial time in hours (0-24, default: 12) */
    initialTime?: number;
    /** Time scale (1 = real-time, 60 = 1 minute = 1 hour) */
    timeScale?: number;
    /** Enable day/night cycle */
    enableDayNightCycle?: boolean;
    /** Skybox configuration */
    skybox?: SkyboxConfig;
    /** Custom time-of-day configurations */
    timeOfDayConfigs?: TimeOfDayConfig[];
    /** Enable fog */
    enableFog?: boolean;
    /** Fog mode (1 = linear, 2 = exponential, 3 = exponential squared) */
    fogMode?: number;
    /** Initial weather */
    weather?: WeatherType;
    /** Physics time acceleration factor (1 = normal) */
    physicsTimeScale?: number;
    /** Time acceleration configuration */
    timeAcceleration?: TimeAccelerationConfig;
}

/**
 * Time state information
 */
export interface TimeState {
    /** Current time in hours (0-24) */
    currentTime: number;
    /** Current time of day period */
    timeOfDay: TimeOfDay;
    /** Current day (starting from 1) */
    day: number;
    /** Normalized time within current period (0-1) */
    periodProgress: number;
    /** Is it currently daytime */
    isDaytime: boolean;
    /** Sun angle in degrees */
    sunAngle: number;
}

/**
 * Environment event types
 */
export type EnvironmentEventType = 
    | "timeChange"
    | "periodChange"
    | "weatherChange"
    | "dayChange";

/**
 * Environment event callback
 */
export type EnvironmentEventCallback = (
    eventType: EnvironmentEventType,
    data: {
        previousValue?: TimeOfDay | WeatherType | number;
        currentValue?: TimeOfDay | WeatherType | number;
        timeState?: TimeState;
    }
) => void;
